import { isAxiosError } from "axios";

import type { UploadSession } from "./types";
import type {
  DocumentLibraryProject,
  SharePointItem,
  SharePointItemVersion
} from "@mj/shared-ui";

import { AZURE_MJ } from "@/lib/axios";

// Every SharePoint call goes through the BFF (`/v3/sharepoint/*`), which
// exchanges our BFF token for a delegated Graph token on-behalf-of the user.
// The browser never calls graph.microsoft.com, holds a Graph-scoped token, or
// knows the drive id — and going through `AZURE_MJ` puts every call behind the
// shared 401/403 refresh-and-retry.
const SHAREPOINT_ENDPOINT = "/v3/sharepoint";

/**
 * The BFF passes Graph SDK models through with their C# PascalCase keys
 * (`Id`, `WebUrl`, `Folder.ChildCount`), not the camelCase Graph REST returns
 * and our types assume — without this, `item.id` is silently `undefined`.
 * Lower-cases the first character of every key, recursively. Idempotent, and
 * keys starting with a non-letter (`@microsoft.graph.downloadUrl`) pass
 * through unchanged, so it stays correct if the BFF ever emits camelCase.
 */
const toCamelCaseKeys = <T>(value: unknown): T => {
  if (Array.isArray(value)) {
    return value.map((item) => toCamelCaseKeys(item)) as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        toCamelCaseKeys(val)
      ])
    ) as T;
  }
  return value as T;
};

const getJson = async <T>(url: string, params?: object): Promise<T> => {
  const { data } = await AZURE_MJ.get(url, { params });
  return toCamelCaseKeys<T>(data);
};

const getBlob = async (url: string): Promise<Blob> => {
  const { data } = await AZURE_MJ.get<Blob>(url, { responseType: "blob" });
  return data;
};

export const sharepointApi = {
  /**
   * The project's root folder, or `null` when it has none yet — a 404 here is
   * an expected answer, not a failure. Anything else is rethrown.
   */
  resolveFolder: async ({
    description,
    opportunityNo
  }: DocumentLibraryProject): Promise<SharePointItem | null> => {
    try {
      return await getJson<SharePointItem>(
        `${SHAREPOINT_ENDPOINT}/folders/resolve`,
        { opportunityNo }
      );
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  },

  getItems: (folderId: string) =>
    getJson<SharePointItem[]>(`${SHAREPOINT_ENDPOINT}/items/${folderId}`),

  getItemVersions: (itemId: string) =>
    getJson<SharePointItemVersion[]>(
      `${SHAREPOINT_ENDPOINT}/items/${itemId}/versions`
    ),

  getItemContent: (itemId: string) =>
    getBlob(`${SHAREPOINT_ENDPOINT}/items/${itemId}/content`),

  getItemVersionContent: (itemId: string, versionId: string) =>
    getBlob(
      `${SHAREPOINT_ENDPOINT}/items/${itemId}/versions/${versionId}/content`
    ),

  /**
   * Starts a Graph upload session for `filename` in `folderId`. Same-name
   * uploads are auto-renamed by Graph rather than overwritten.
   */
  createUploadSession: async (
    folderId: string,
    filename: string
  ): Promise<UploadSession> => {
    const { data } = await AZURE_MJ.post(
      `${SHAREPOINT_ENDPOINT}/items/${folderId}/upload-session`,
      null,
      { params: { filename } }
    );
    return toCamelCaseKeys<UploadSession>(data);
  }
};
